import { AfterViewInit, Component, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { AppSettings } from '@lsq-frontend/fasttrack-shared/environment';
import { atlas, INVOICE_UPLOAD_HISTORY } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { FileType } from '@lsq-frontend/remote-services/enterprise/common';
import {
    InvoiceBatchUploadMethod,
    ListInvoiceBatchImagesRequest,
    ListInvoiceBatchImagesService,
    SendMobileUploadLinkRequest
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    Color,
    forLifeOf,
    IBannerTabStep,
    LifecycleAware,
    Masks,
    Modal,
    Paging,
    Unmasks
} from '@lsq-frontend/common-ui';
import {
    getRouterLinkUrlById,
    Icons,
    PhonePipe,
    ResponsiveService,
    WindowReferenceService,
    WorkflowMonitorService
} from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { isWeekend } from 'date-fns';
import { convertToTimeZone } from 'date-fns-timezone';
import { combineLatest, merge, Observable, of as observableOf } from 'rxjs';
import { filter, first, map, shareReplay, startWith, takeWhile } from 'rxjs/operators';
import {
    IInvoiceUploadMethodOption,
    InvoiceFileUpload,
    InvoiceUploadFlowStep
} from '../invoices.models';
import { InvoiceBatchService } from './invoice-batch.service';
import { SmsUploadService } from './sms-upload.service';
import { UploadInvoicesCoverSheetComponent } from './upload-invoices-cover-sheet/upload-invoices-cover-sheet.component';
import { UploadInvoicesService } from './upload-invoices.service';
import Bugsnag from '@bugsnag/js';

// tslint:disable:no-floating-promises

export interface IInvoiceUploadFlowStep extends Partial<IBannerTabStep> {
    title?: string;
    step: InvoiceUploadFlowStep;
}

@LifecycleAware()
@Component({
    selector: 'lsq-upload-invoices',
    templateUrl: './upload-invoices.component.html',
    styleUrls: ['./upload-invoices.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class UploadInvoicesComponent implements OnDestroy, AfterViewInit {
    private _isAlive = false;

    // Queries.
    @ViewChild(UploadInvoicesCoverSheetComponent)
    public uploadInvoicesCoverSheet: UploadInvoicesCoverSheetComponent;

    // File uploads.
    public fileInputElement: HTMLInputElement;
    public invoiceFileUploads: InvoiceFileUpload[] = [];
    public uploadedInvoicesCount?: number;

    // UI state.
    public uploadMethodOptions: IInvoiceUploadMethodOption[] = [
        {
            method: InvoiceBatchUploadMethod.InvoiceAndBackupCombinedUpload,
            icon: Icons.CombinedDocs,
            isRecommended: true,
            title: this._i18n({
                value: `Invoices and Supporting Documents`,
                meaning: `Label for the 'invoices and supporting documents' option for uploading invoices.`
            }),
            description: this._i18n({
                value: `Upload invoices and supporting documents as separate images.`,
                meaning: `Describes the 'invoices and supporting documents' option for uploading invoices.`
            })
        },
        {
            method: InvoiceBatchUploadMethod.SpreadsheetAndBackupUpload,
            icon: Icons.Spreadsheet,
            title: this._i18n({
                value: `Spreadsheet`,
                meaning: `Label for the 'spreadsheet' option for uploading invoices.`
            }),
            description: this._i18n({
                value: `Upload a single spreadsheet detailing your invoices.`,
                meaning: `Describes the 'spreadsheet' option for uploading invoices.`
            })
        }
    ];
    public availableUploadMethodOptions: IInvoiceUploadMethodOption[] = [];
    // TODO(dmayer): Set this flag to `true` only after getting a response from
    // `GetUploadInvoiceBatchPreferenceService`.
    public didCheckForUploadMethodPreference = true;
    public flowSteps: IInvoiceUploadFlowStep[] = [
        { step: InvoiceUploadFlowStep.UploadOptionsSelection },
        {
            title: this._i18n({
                value: 'Cover Sheet',
                meaning: `Heading for the 'cover sheet' step of the invoice upload flow.`
            }),
            step: InvoiceUploadFlowStep.CoverSheet
        },
        {
            title: this._i18n({
                value: 'Invoices and Supporting Documents',
                meaning: `Heading for the 'upload' step of the invoice upload flow.`
            }),
            step: InvoiceUploadFlowStep.Upload
        },
        {
            title: this._i18n({
                value: 'Thank You',
                meaning: `Heading for the final 'thank you' screen of the invoice upload flow.`
            }),
            step: InvoiceUploadFlowStep.Success
        },
        {
            title: this._i18n({
                value: 'Error',
                meaning: `Heading for the final screen of the invoice upload flow, if an error occurred.`
            }),
            step: InvoiceUploadFlowStep.Failure
        }
    ];
    public currentFlowStep: IInvoiceUploadFlowStep = this.flowSteps[0];
    public mobileLinkSent = false;
    public imagePollInterval: any;
    public isDraggedOver: boolean;
    public mobileFormGroup = new FormGroup({
        phone: new FormControl('', [Validators.required, LsqValidators.phone])
    });
    public isSubmittingInvoiceBatch = false;
    public lastStatedTotalStream: Observable<number>;
    public invoiceSuccessMessage: String;

    // Footer state.
    public disableContinueStream: Observable<boolean>;
    public disableSubmitStream: Observable<boolean>;

    // Constants.
    public icons = Icons;
    public color = Color;
    public invoiceUploadFlowStep = InvoiceUploadFlowStep;
    public invoiceBatchUploadMethod = InvoiceBatchUploadMethod;
    public masks = Masks;
    public phoneUnmask = Unmasks.PhoneUnmask;

    constructor(
        public uploadInvoicesService: UploadInvoicesService,
        public invoiceBatchService: InvoiceBatchService,
        public responsiveService: ResponsiveService,
        private _router: Router,
        private _userService: UserService,
        private _listInvoiceBatchImagesService: ListInvoiceBatchImagesService,
        private _appSettings: AppSettings,
        private _workflowMonitorService: WorkflowMonitorService,
        private _windowReferenceService: WindowReferenceService,
        private _alertService: AlertService,
        private _modalService: ModalService,
        private _phonePipe: PhonePipe,
        private _smsUploadService: SmsUploadService,
        private _i18n: I18n
    ) {}

    public async ngOnInit(): Promise<void> {
        this._isAlive = true;

        this._smsUploadService.getMobileNumberState().then((number) => {
            this.mobileFormGroup.get('phone').setValue(number);
        });

        this.disableContinueStream = merge(
            observableOf(true),
            combineLatest(
                this.uploadInvoicesService.invoiceSummaryIsValidSubject,
                this.invoiceBatchService.coverSheetFormIsValidSubject
            ).pipe(
                map(
                    ([invoiceSummaryIsValid, coverSheetFormIsValid]) =>
                        !invoiceSummaryIsValid || !coverSheetFormIsValid
                )
            )
        );

        this.lastStatedTotalStream = this.invoiceBatchService.coverSheetFormValueSubject.pipe(
            filter((value) => value && !!(value.totalAmount || value.totalAmount === 0)),
            map(({ totalAmount }) => totalAmount)
        );
    }

    public ngAfterViewInit(): void {
        this.navigateToStart();
    }

    public ngOnDestroy(): void {
        this._isAlive = false;
        this._reset();
    }

    // Getters.
    public get acceptContentTypes(): string {
        return this.uploadInvoicesService.allowedContentTypes.join(',');
    }

    public get title(): string {
        if (
            this.currentFlowStep &&
            (this.currentFlowStep.step === InvoiceUploadFlowStep.Success ||
                this.currentFlowStep.step === InvoiceUploadFlowStep.Failure)
        ) {
            return this.currentFlowStep.title;
        } else {
            return this._i18n({
                value: 'Upload Invoices',
                meaning: `Heading for the 'Upload Invoices' page`
            });
        }
    }

    // Event handlers and state mutations.
    public handleUploadMethodSelect(uploadMethodOption: IInvoiceUploadMethodOption): void {
        this.uploadInvoicesService.selectUploadMethodOption(uploadMethodOption);
        this.currentFlowStep = this.flowSteps[0];
        this.handleNavigate(Paging.Next);
        this._reset();
    }

    public handleNavigate(direction: Paging): void {
        const currentStepIndex = this.flowSteps.indexOf(this.currentFlowStep);
        let newStepIndex = currentStepIndex + direction;
        let allowNavigate = false;
        // Pre-navigate.
        if (newStepIndex === -1) {
            newStepIndex = 0;
        }
        if (newStepIndex >= 0 && newStepIndex <= this.flowSteps.length) {
            if (
                this.flowSteps[newStepIndex].step === InvoiceUploadFlowStep.UploadOptionsSelection
            ) {
                if (this._workflowMonitorService.isWorkflowInProgress) {
                    this._workflowMonitorService.warn(() => {
                        this.currentFlowStep = this.flowSteps[newStepIndex];
                    });
                } else {
                    allowNavigate = true;
                }
            } else {
                allowNavigate = true;
            }
        }

        if (
            this.flowSteps[newStepIndex].step === InvoiceUploadFlowStep.Success ||
            this.flowSteps[newStepIndex].step === InvoiceUploadFlowStep.Failure
        ) {
            this._workflowMonitorService.markWorkflowFinished();
        }

        // Navigate.
        if (allowNavigate) {
            this.currentFlowStep = this.flowSteps[newStepIndex];
            this._windowReferenceService.scrollToTop();
        }

        // Post-navigate.
        if (this.currentFlowStep.step === InvoiceUploadFlowStep.Upload) {
            if (!this.invoiceFileUploads.length) {
                this._resetInvoiceUploader();
                this._initUploader();
            }
        }
    }

    public async navigateToStart(): Promise<void> {
        await this._initializeFlow();
    }

    public async startOver(): Promise<void> {
        this._reset();
        return this.navigateToStart();
    }

    public navigateToFailureScreen(): void {
        this.currentFlowStep =
            this.flowSteps[
                this.flowSteps.findIndex(({ step }) => step === InvoiceUploadFlowStep.Failure)
            ];
    }

    public goToUploadHistory(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, INVOICE_UPLOAD_HISTORY));
    }

    public async handleSubmit(): Promise<void> {
        const fileUploads = [
            ...this.uploadInvoicesService.invoiceFileUploads,
            this.uploadInvoicesService.invoiceSummaryFileUpload
        ].filter((fileUpload) => !!fileUpload);

        // Submit only if `CompleteInvoiceBatchImageUpload` has been called successfully
        // for every file upload.
        if (fileUploads.every((fileUpload) => fileUpload.isUploadStatusComplete)) {
            this.uploadInvoicesService.invoiceBatchImageAdditionalUploadsMap.clear();
            try {
                this.isSubmittingInvoiceBatch = true;
                this.uploadedInvoicesCount = await this.uploadInvoicesService.submitInvoiceBatch(
                    this.invoiceFileUploads
                );
                this.isSubmittingInvoiceBatch = false;

                this.handleNavigate(Paging.Next);
                if (this.imagePollInterval) {
                    clearInterval(this.imagePollInterval);
                    delete this.imagePollInterval;
                }
            } catch (error) {
                this.navigateToFailureScreen();
                this.isSubmittingInvoiceBatch = false;
                Bugsnag.notify(error);
                throw error;
            }
        } else {
            this._alertService.info(
                `Some uploads are still pending. Please wait a few seconds and try again.`
            );
        }
    }

    public handleFileSelectLinkClick(): void {
        if (this.canUpload()) {
            this.fileInputElement.click();
        }
    }

    // UI updates.
    private async _initializeFlow(): Promise<void> {
        this.availableUploadMethodOptions =
            await this.invoiceBatchService.getAvailableUploadMethodOptions(
                this.uploadMethodOptions
            );

        if (this.availableUploadMethodOptions.length === 1) {
            // If there's only one upload method available, select it and navigate to the cover sheet.
            this.handleUploadMethodSelect(this.availableUploadMethodOptions[0]);
        } else {
            // Else, navigate to the first step in the flow.
            this.currentFlowStep = this.flowSteps.find(
                ({ step }) => step === InvoiceUploadFlowStep.UploadOptionsSelection
            );
        }
    }

    public getInvoiceSuccessMessage(): string {
        const timeInEST = convertToTimeZone(new Date(), { timeZone: 'America/New_York' });
        const isAfternoon = !(timeInEST.getHours() < 12);

        if (isWeekend(timeInEST) || isAfternoon) {
            // Currently afternoon on a business day.
            return this._i18n({
                value: 'You can expect your invoice submission to be processed within the next business day.',
                meaning: `After the upload succeeds, tells the user when to expect the invoices to be processed`
            });
        } else {
            // Currently a business day before 12 PM ET.
            return this._i18n({
                value: 'You can expect your invoice submission to be processed by the end of the day.',
                meaning: `After the upload succeeds, tells the user when to expect the invoices to be processed`
            });
        }
    }

    private _reset(): void {
        this.uploadInvoicesService.reset();
        this.invoiceBatchService.coverSheetFormGroup.reset();
        this.invoiceFileUploads = [];
        this.mobileLinkSent = false;
        if (this.imagePollInterval) {
            clearInterval(this.imagePollInterval);
            delete this.imagePollInterval;
        }
    }

    private _resetInvoiceUploader(): void {
        this.uploadInvoicesService.resetInvoiceUploader();
        this.invoiceFileUploads = [];
    }

    private _initUploader(): void {
        const { inputElement } = this.uploadInvoicesService.initInvoiceFileUploadsStream(
            this._smsUploadService.getCompletedUploadsStream()
        );

        this._listInvoiceBatchImagesService.errorStream.pipe(forLifeOf(this)).subscribe(
            () => {
                if (this.imagePollInterval) {
                    clearInterval(this.imagePollInterval);
                    delete this.imagePollInterval;
                }
            },
            (error) => {
                Bugsnag.notify(error);
            }
        );

        this.fileInputElement = inputElement;

        this.uploadInvoicesService.invoiceFileUploadsRef.fileUploadsStream
            .pipe(
                forLifeOf(this),
                takeWhile(() => this._isAlive)
            )
            .subscribe((fileUploads) => {
                if (fileUploads && fileUploads.length) {
                    this._workflowMonitorService.markWorkflowStarted(
                        'Your invoice submission has not been uploaded yet. The files will not be saved if you navigate away from this page.',
                        'Navigate away',
                        'Keep working'
                    );
                    // Maintain the order if possible.
                    const fileUploadIdsInOrder = this.invoiceFileUploads.map(
                        (fileUpload) => fileUpload.id
                    );
                    const oldFileUploads: InvoiceFileUpload[] = [];
                    const newFileUploads: InvoiceFileUpload[] = fileUploads.filter(
                        (fileUpload) => fileUploadIdsInOrder.indexOf(fileUpload.id) === -1
                    );
                    fileUploadIdsInOrder.forEach((id) => {
                        const fileUpload = fileUploads.find((x) => x.id === id);
                        if (fileUpload) {
                            oldFileUploads.push(fileUpload);
                        }
                    });
                    if (
                        this.invoiceBatchService.isSpreadsheetUploadMethodSelected() &&
                        this.invoiceFileUploads.length !== 0
                    ) {
                        // If the user is uploading additional files via spreadsheet upload,
                        // we only ever want to show a maximum of one document on the page view.
                        this.invoiceFileUploads = [...oldFileUploads];
                    } else {
                        this.invoiceFileUploads = [...oldFileUploads, ...newFileUploads];
                    }
                } else {
                    this.invoiceFileUploads = [];
                }
            });

        this.uploadInvoicesService.invoicesUploadZoneDraggedOverStream
            .pipe(forLifeOf(this))
            .subscribe((isDraggedOver) => {
                this.isDraggedOver = isDraggedOver;
            });

        this.disableSubmitStream = combineLatest(
            this.uploadInvoicesService.invoiceFileUploadsRef.fileUploadsStream.pipe(
                map(
                    (fileUploads) =>
                        !fileUploads.length ||
                        fileUploads.some(
                            (upload) =>
                                !upload.isUploadStatusComplete ||
                                upload.linkedFileUploads.some(
                                    (linkedUpload) => !linkedUpload.isUploadStatusComplete
                                )
                        )
                ),
                shareReplay(1)
            ),
            this.uploadInvoicesService.invoiceSummaryFileUploadRef.fileUploadsStream.pipe(
                map((fileUpload) => fileUpload && !fileUpload.isUploadStatusComplete),
                shareReplay(1)
            ),
            // If 'Continue' should be disabled, so should 'Submit'.
            // This covers the edge case of a user clicking 'Continue' after
            // selecting an invoice summary file, but before the upload has started.
            this.disableContinueStream.pipe(shareReplay(1))
        ).pipe(
            map((areInvalid) => areInvalid.some((isInvalid) => isInvalid)),
            startWith(true)
        );
    }

    // Boolean methods.
    public isCoverSheetStep(): boolean {
        return (
            this.currentFlowStep && this.currentFlowStep.step === InvoiceUploadFlowStep.CoverSheet
        );
    }

    public isUploadStep(): boolean {
        return this.currentFlowStep && this.currentFlowStep.step === InvoiceUploadFlowStep.Upload;
    }

    public bannerStepTabsShouldDisplay(): boolean {
        return (
            this.currentFlowStep &&
            (this.currentFlowStep.step === InvoiceUploadFlowStep.CoverSheet ||
                this.currentFlowStep.step === InvoiceUploadFlowStep.Upload ||
                this.currentFlowStep.step === InvoiceUploadFlowStep.Success)
        );
    }

    public isSuccessScreen(): boolean {
        return this.currentFlowStep && this.currentFlowStep.step === InvoiceUploadFlowStep.Success;
    }

    public isFailureScreen(): boolean {
        return this.currentFlowStep && this.currentFlowStep.step === InvoiceUploadFlowStep.Failure;
    }

    public canUpload(): boolean {
        return (
            !this.invoiceBatchService.isSpreadsheetUploadMethodSelected() ||
            this.invoiceFileUploads.length === 0
        );
    }

    public canSendMobileLink(): boolean {
        return !this.mobileFormGroup.invalid && this.canUpload();
    }

    public shouldDisplayBackButton(): boolean {
        return !(
            this.isSuccessScreen() ||
            (this.isCoverSheetStep() && this.availableUploadMethodOptions.length <= 1)
        );
    }

    // API calls.
    public async sendSmsMessage(): Promise<void> {
        if (this.mobileLinkSent) {
            // If they are clicking for a second (or more) time, show a toast, since no other visual feedback.
            this._alertService.success(
                `A new link has been sent to ${this.mobileFormGroup.value.phone}.`
            );
        }
        this.mobileLinkSent = true;
        if (!this.invoiceBatchService.invoiceBatchKey) {
            await this.invoiceBatchService.createInvoiceBatch();
        }

        const user = await this._userService.user$.pipe(first()).toPromise();
        const request = new SendMobileUploadLinkRequest({
            clientName: user.currentCompany.name,
            invoiceBatchKey: this.invoiceBatchService.invoiceBatchKey,
            phone: this.mobileFormGroup.value.phone
        });
        this._smsUploadService.sendMessage(request);
        if (!this.imagePollInterval) {
            // Start polling, if not already doing so.
            const listRequest = new ListInvoiceBatchImagesRequest({
                invoiceBatchKey: this.invoiceBatchService.invoiceBatchKey,
                width: 330,
                format: FileType.Jpg
            });
            this.imagePollInterval = setInterval(() => {
                this._listInvoiceBatchImagesService.execute(listRequest);
            }, this._appSettings.mobileUploadPollingInterval);
        }

        this._modalService.showModal(
            new Modal({
                id: 'sms-message-sent',
                title: 'Mobile Upload',
                bodyHtml: `<p>The upload link has been sent to ${this._phonePipe.transform(
                    this.mobileFormGroup.value.phone
                )}.
            The link will remain active for the next 10 minutes.
            <br>
            Please do not navigate away from this page until all images sent from the mobile phone appear.</p>`,
                showFooter: true,
                showOkButton: true,
                okButtonText: 'Ok'
            })
        );
    }
}
